import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Crosshair, Search, Plus, Edit2, Trash2,
  Calendar, AlertCircle, TrendingUp, X, Loader,
  Briefcase, BarChart3, Download, Filter, Activity,
  ArrowUpRight, ChevronRight, ShieldCheck, Zap, Info,
  Globe, Clock, MoreVertical
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency, getCurrencySymbol, convertToBase } from '../../utils/currency';
import { formatDate } from '../../utils/formatDate';
import { useCompanyCurrency, useCurrencyRates } from '../../hooks/useCompanySettings';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

// Must match the real Deal.stage values (see DealsPage.jsx DEFAULT_STAGES) —
// this previously used an unrelated vocabulary (contacted/closing/won) that never
// matched an actual deal, so every deal fell back to a default "Discovery" badge.
const STAGES = {
  new:          { label: 'New',          badge: 'sf-badge-neutral' },
  qualified:    { label: 'Qualified',    badge: 'sf-badge-neutral' },
  proposal:     { label: 'Proposal',     badge: 'sf-badge-warning' },
  negotiation:  { label: 'Negotiation',  badge: 'sf-badge-warning' },
  closed_won:   { label: 'Closed Won',   badge: 'sf-badge-success' },
  closed_lost:  { label: 'Closed Lost',  badge: 'sf-badge-error' },
};

const STAGE_FILTER_OPTIONS = [
  { value: 'active', label: 'Active (not closed)' },
  { value: 'all', label: 'All Stages' },
  ...Object.entries(STAGES).map(([value, { label }]) => ({ value, label })),
];

export default function OpportunitiesPage() {
  const navigate = useNavigate();
  const companyCurrency = useCompanyCurrency();
  const ratesCtx = useCurrencyRates();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('active');
  const [showStageFilter, setShowStageFilter] = useState(false);
  const [sortByValue, setSortByValue] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '', value: 0, probability: 50, stage: 'new', expectedCloseDate: '', contactId: ''
  });

  useEffect(() => { fetchDeals(); }, []);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      // No filter/pagination UI here yet — request a high limit so the view doesn't
      // silently truncate at the backend's default page size (20).
      const response = await api.get('/crm/deals', { params: { limit: 1000 } });
      setDeals(response.data.deals || response.data || []);
    } catch (err) {
      alertService.error('Sync Error', 'Failed to synchronize opportunity registry.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = deals.filter(d => {
      const matchSearch = (d.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (d.accountId?.name || '').toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchSearch) return false;
      if (stageFilter === 'active') return d.stage !== 'closed_won' && d.stage !== 'closed_lost';
      if (stageFilter === 'all') return true;
      return d.stage === stageFilter;
    });
    if (sortByValue) {
      result = [...result].sort((a, b) => (b.value || 0) - (a.value || 0));
    }
    return result;
  }, [deals, searchQuery, stageFilter, sortByValue]);

  const activeDeals = deals.filter(d => d.stage !== 'closed_won' && d.stage !== 'closed_lost');
  const closedWonDeals = deals.filter(d => d.stage === 'closed_won');

  // Each deal is converted into the base currency before summing — otherwise a
  // USD deal and an INR deal just get added as raw numbers under one symbol.
  const totalPipeline = activeDeals.reduce((sum, d) => sum + convertToBase(d.value || 0, d.currency, ratesCtx), 0);
  const weightedForecast = activeDeals.reduce((sum, d) => sum + (convertToBase(d.value || 0, d.currency, ratesCtx) * (d.probability || 50) / 100), 0);
  // Aggregate totals are labeled with the company's base currency (from Settings),
  // not an individual deal's currency — otherwise the symbol never reflects a
  // baseCurrency change and is misleading whenever deals span multiple currencies.
  const pipelineCurrency = companyCurrency;

  // Win probability: average confidence across deals still open, not a fixed number.
  // Uses the same || 50 fallback as weightedForecast above so a deal with no
  // probability set yet isn't treated as 50% confidence in one KPI and 0% in another.
  const winProbability = activeDeals.length
    ? Math.round(activeDeals.reduce((s, d) => s + (d.probability || 50), 0) / activeDeals.length)
    : 0;

  // Avg deal cycle: days from creation to close, averaged over deals actually won so far
  const avgDealCycle = (() => {
    const cycles = closedWonDeals
      .filter(d => d.createdAt && d.actualCloseDate)
      .map(d => (new Date(d.actualCloseDate) - new Date(d.createdAt)) / (1000 * 60 * 60 * 24));
    if (!cycles.length) return null;
    return Math.round(cycles.reduce((s, c) => s + c, 0) / cycles.length);
  })();

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      if (editingId) {
        await api.put(`/crm/deals/${editingId}`, formData);
        alertService.success('Updated', 'Opportunity profile synchronized');
      } else {
        await api.post('/crm/deals', formData);
        alertService.success('Created', 'New opportunity node initialized');
      }
      setShowAddModal(false);
      fetchDeals();
    } catch (err) {
      alertService.error('Error', "Couldn't save the opportunity. Try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (deal) => {
    const confirmed = await alertService.confirm('Delete Opportunity?', `Delete "${deal.title}"?`, 'danger', 'Delete');
    if (confirmed) {
      try {
        await api.delete(`/crm/deals/${deal._id}`);
        alertService.success('Deleted', 'Opportunity identity archived');
        fetchDeals();
      } catch (err) { alertService.error('Error', 'Dissolution failure.'); }
    }
  };

  const handleExportPipeline = async () => {
    try {
      // Opportunities means open pipeline only — won/lost deals belong to
      // reporting, not this export, regardless of which pipeline they're in.
      const response = await api.get('/crm/deals/export', {
        responseType: 'blob',
        params: { excludeStatus: 'won,lost' },
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `opportunities-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export pipeline');
    }
  };

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader title="Opportunity Pipeline" subtitle={<>{activeDeals.length} Open opportunities in your pipeline</>} icon={Crosshair} actions={<>
          <button onClick={handleExportPipeline} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2">
            <Download size={14} /> Export Pipeline
          </button>
          <button onClick={() => { setEditingId(null); setFormData({ title: '', value: 0, probability: 50, stage: 'new', expectedCloseDate: '', contactId: '' }); setShowAddModal(true); }} className="h-9 px-4 bg-[#0176D3] text-white rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2">
            <Plus size={14} /> New Opportunity
          </button>
      </>} />

      {/* KPI Matrix */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total Pipeline</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{formatCurrency(totalPipeline, pipelineCurrency)}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Weighted Forecast</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{formatCurrency(weightedForecast, pipelineCurrency)}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Win Probability</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{winProbability}%</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#a78bfa]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Avg. Deal Cycle</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{avgDealCycle !== null ? `${avgDealCycle}d` : 'N/A'}</h3>
        </div>
      </div>

      {/* Control Bar */}
      <div className="sf-card p-4 bg-[#F4F6F9] border-[#D8DDE6]">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1 w-full relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
            <input
              type="text"
              placeholder="Search opportunities by title or account…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all"
            />
          </div>
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setShowStageFilter(v => !v)}
              title="Filter by stage"
              className={cn("p-2 rounded transition-colors", stageFilter !== 'active' ? 'text-[#0176D3] bg-[#E0F0FD]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <Filter size={18} />
            </button>
            {showStageFilter && (
              <div className="absolute top-full right-10 mt-2 z-20 bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] py-1 w-56">
                {STAGE_FILTER_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setStageFilter(opt.value); setShowStageFilter(false); }}
                    className={cn(
                      "w-full text-left px-4 py-2 text-[12px] font-bold hover:bg-[#F3F2F2] transition-colors",
                      stageFilter === opt.value ? 'text-[#0176D3]' : 'text-[#2E2E2E]'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setSortByValue(v => !v)}
              title="Sort by value"
              className={cn("p-2 rounded transition-colors", sortByValue ? 'text-[#0176D3] bg-[#E0F0FD]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <BarChart3 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Pipeline Table */}
      <div className="sf-table-container">
        <table className="sf-table">
          <thead>
            <tr>
              <th className="pl-6">Opportunity Identity</th>
              <th>Pipeline Valuation</th>
              <th>Confidence Score</th>
              <th>Commercial stage</th>
              <th>Target Close</th>
              <th className="text-right pr-6">Operations</th>
            </tr>
          </thead>
          <tbody>
            {loading && deals.length === 0 ? (
              <tr><td colSpan={6} className="py-24 text-center"><Loader size={24} className="animate-spin mx-auto text-[#0176D3]" /></td></tr>
            ) : filtered.length > 0 ? filtered.map(deal => {
              const stage = STAGES[deal.stage] || STAGES.new;
              return (
                <tr key={deal._id} className="group">
                  <td className="pl-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded bg-[#E0F0FD] flex items-center justify-center text-[#0176D3]">
                        <Briefcase size={16} />
                      </div>
                      <div>
                        <p className="font-bold text-[#2E2E2E] group-hover:text-[#0176D3] transition-colors cursor-pointer uppercase italic tracking-tight" onClick={() => navigate(`/dashboard/crm/deals/${deal._id}`)}>{deal.title}</p>
                        <p className="text-[10px] text-[#706E6B] font-bold">{deal.accountId?.name || 'No account'}</p>
                      </div>
                    </div>
                  </td>
                  {/* Normalized to the company's base currency — a list mixing each deal's
                      own raw currency ($500 next to ₹1K) reads as broken, not just inconsistent. */}
                  <td className="py-4 font-bold text-[#2E844A] tabular-nums">{formatCurrency(convertToBase(deal.value, deal.currency, ratesCtx), companyCurrency)}</td>
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-1.5 bg-[#F3F2F2] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0176D3]" style={{ width: `${deal.probability || 50}%` }} />
                      </div>
                      <span className="text-[10px] font-bold text-[#706E6B] tabular-nums">{deal.probability || 50}%</span>
                    </div>
                  </td>
                  <td className="py-4"><span className={cn("sf-badge", stage.badge)}>{stage.label}</span></td>
                  <td className="py-4 text-[11px] font-medium text-[#706E6B]">{deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : 'TBD'}</td>
                  <td className="pr-6 py-4 text-right">
                    <RowActionsMenu actions={[
                      { label: 'Edit', icon: Edit2, onClick: () => { setEditingId(deal._id); setFormData(deal); setShowAddModal(true); } },
                      { label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(deal) },
                    ]} />
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan={6} className="py-24 text-center text-[#706E6B] font-bold opacity-20">Zero opportunity matches</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Profile */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editingId ? 'Edit Opportunity' : 'New Opportunity'}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[#706E6B] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Opportunity Title</label>
                <input required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Valuation ({getCurrencySymbol(companyCurrency)})</label>
                  <input type="number" required min="0" step="0.01" value={formData.value} onChange={e => setFormData(p => ({ ...p, value: parseFloat(e.target.value) }))} className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Confidence (%)</label>
                  <input type="number" value={formData.probability} onChange={e => setFormData(p => ({ ...p, probability: parseInt(e.target.value) }))} className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Commercial stage</label>
                  <select value={formData.stage} onChange={e => setFormData(p => ({ ...p, stage: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all">
                    {Object.entries(STAGES).map(([k, v]) => <option key={k} value={k}>{v.label.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Close Date</label>
                  <input type="date" value={formData.expectedCloseDate} onChange={e => setFormData(p => ({ ...p, expectedCloseDate: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] transition-all" />
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
